import { CheckIcon, ChevronsUpDownIcon, GlobeIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { isCommonZone, listTimeZones, tzOffsetLabel, type TimeZoneOption } from './schedule';

export interface ZoneSuggestion {
  id: string;
  /** Why it is suggested: "This job", "Your organization", "Your computer". */
  label: string;
}

interface Props {
  value: string;
  onChange: (tz: string) => void;
  id?: string;
  suggestions?: ZoneSuggestion[];
  className?: string;
  disabled?: boolean;
}

/** Searchable IANA time zone picker: suggested zones first (job, organization, this computer, US), then all, with live offsets. */
export function TimeZonePicker({ value, onChange, id, suggestions = [], className, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const zones = useMemo(() => listTimeZones(), []);
  const byId = useMemo(() => new Map(zones.map((z) => [z.id, z])), [zones]);
  const suggested = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<TimeZoneOption & { why?: string }> = [];
    for (const s of suggestions) {
      const z = byId.get(s.id);
      if (z && !seen.has(z.id)) {
        seen.add(z.id);
        out.push({ ...z, why: s.label });
      }
    }
    for (const z of zones) if (isCommonZone(z.id) && !seen.has(z.id)) {
      seen.add(z.id);
      out.push(z);
    }
    return out;
  }, [zones, byId, suggestions]);
  const current = byId.get(value);
  const currentOffset = current?.offset ?? (value ? tzOffsetLabel(value).label : '');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button id={id} type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled} className={cn('h-9 w-full justify-between font-normal', className)}>
          <span className="flex min-w-0 items-center gap-2">
            <GlobeIcon className="text-muted-foreground size-4 shrink-0" aria-hidden />
            <span className="truncate">{current ? `${current.city}${current.region ? ` (${current.region})` : ''}` : value || 'Choose a time zone'}</span>
          </span>
          <span className="flex items-center gap-2">
            {currentOffset && <span className="text-muted-foreground hidden text-xs sm:inline">{currentOffset}</span>}
            <ChevronsUpDownIcon className="size-4 opacity-50" aria-hidden />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search city or region…" />
          <CommandList className="max-h-72">
            <CommandEmpty>No time zone matches.</CommandEmpty>
            <CommandGroup heading="Suggested">
              {suggested.map((z) => (
                <ZoneItem key={z.id} zone={z} why={z.why} selected={z.id === value} onSelect={() => { onChange(z.id); setOpen(false); }} />
              ))}
            </CommandGroup>
            <CommandGroup heading="All time zones">
              {zones.map((z) => (
                <ZoneItem key={z.id} zone={z} selected={z.id === value} onSelect={() => { onChange(z.id); setOpen(false); }} />
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ZoneItem({ zone, why, selected, onSelect }: { zone: TimeZoneOption; why?: string; selected: boolean; onSelect: () => void }) {
  return (
    <CommandItem value={`${zone.id} ${zone.city} ${zone.region} ${zone.offset} ${why ?? ''}`} onSelect={onSelect} className="gap-2">
      <CheckIcon className={cn('size-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} aria-hidden />
      <span className="min-w-0 flex-1 truncate">
        {zone.city}
        {zone.region && <span className="text-muted-foreground"> · {zone.region}</span>}
        {why && <span className="text-muted-foreground text-xs"> — {why}</span>}
      </span>
      <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{zone.offset}</span>
    </CommandItem>
  );
}
